import { FormLabel, Stack } from "@mui/material";

import { useCallback, useMemo } from "react";
import { genericMemo } from "../../../utils/utils";
import IconButton from "./IconButton";
import { IItem, ISelectIconProps } from "./model";

const getFormValue = <T extends IItem>(data: T): T => {
  const { icon, ...formValue } = data;

  return formValue as T;
};

const SelectIcon = <T extends IItem>({
  options,
  label,
  addIcon,
  ...props
}: ISelectIconProps<T>) => {
  const memoizedProps = useMemo(() => props, [props]);

  const { onChange, value, multi } = memoizedProps;

  const onClickHandler = useCallback(
    (data: T) => {
      const formValue = getFormValue(data);

      if (multi) {
        if (!value) {
          onChange([formValue]);
        } else {
          const index = value.findIndex((d) => d.id === data.id);

          if (index === -1) {
            onChange([...value, formValue]);
            return;
          }

          onChange(value.filter((d) => d.id !== data.id));
        }
      } else if (multi === false) {
        if (value?.id === data.id) {
          onChange(null);
        } else {
          onChange(formValue);
        }
      }
    },
    [onChange, value, multi]
  );

  const isSelected = useCallback(
    (v: T): boolean => {
      if (multi) {
        return !!value?.find((pV) => pV.id === v.id);
      } else if (multi === false) {
        return value?.id === v.id;
      }

      return false;
    },
    [value, multi]
  );

  return (
    <Stack gap={1}>
      {label && <FormLabel>{label}</FormLabel>}
      <Stack flexDirection={"row"} gap={1}>
        {options.map((o) => (
          <IconButton
            item={o}
            onClick={onClickHandler}
            selected={isSelected(o)}
            key={o.id}
          />
        ))}
        {addIcon && addIcon}
      </Stack>
    </Stack>
  );
};

export default genericMemo(SelectIcon);
